import * as RTabs from '@radix-ui/react-tabs'
import type { HTMLAttributes, ReactNode } from 'react'
import { cx } from './cx'
import s from './Tabs.module.css'

/**
 * Tabs — underline tabs on Radix Tabs (roving tabindex, arrow-key nav, aria for
 * free). One skin replaces the hand-rolled `.vtab` / `.tab` variants. Use with
 * external state (view switching) — Tabs.Panel is optional.
 *
 *   <Tabs value={v} onValueChange={setV}>
 *     <Tabs.List><Tabs.Tab value="a">A</Tabs.Tab>…</Tabs.List>
 *     <Tabs.Panel value="a">…</Tabs.Panel>
 *   </Tabs>
 *
 * ── `Tabs.Panel` is optional in the MARKUP and not in the ARIA ──────────────
 * Radix puts `aria-controls` on every trigger, pointing at the id its Content
 * would have. Render the panels somewhere else — a sibling `{tab === 'a' && …}`
 * outside this root is the tempting shape — and those ids resolve to nothing:
 * axe reports `aria-valid-attr-value` at CRITICAL, and a screen reader is told
 * each tab controls a region that does not exist.
 *
 * Measured on `/ops/estate` (#2284), where six tabs controlled six nothings and
 * the a11y gate caught it the first time that route was scanned. Radix renders
 * only the ACTIVE panel, so moving the content inside costs nothing: the
 * conditional becomes the control's instead of the page's.
 */
export function Tabs(
  { value, onValueChange, activationMode = 'manual', className, children }:
  { value: string; onValueChange: (v: string) => void; activationMode?: 'automatic' | 'manual'; className?: string; children: ReactNode },
) {
  return (
    <RTabs.Root value={value} onValueChange={onValueChange} activationMode={activationMode} className={className}>
      {children}
    </RTabs.Root>
  )
}

function List({ children, className, ...rest }: HTMLAttributes<HTMLDivElement> & { children: ReactNode }) {
  return <RTabs.List className={cx(s.list, className)} {...rest}>{children}</RTabs.List>
}
function Tab({ value, children, className, ...rest }: HTMLAttributes<HTMLButtonElement> & { value: string; children: ReactNode }) {
  return <RTabs.Trigger value={value} className={cx(s.tab, className)} {...rest}>{children}</RTabs.Trigger>
}
function Panel({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  return <RTabs.Content value={value} className={className}>{children}</RTabs.Content>
}

Tabs.List = List
Tabs.Tab = Tab
Tabs.Panel = Panel
